import axios from 'axios';
import { getAuthHeader, getUserId, getUserInfo as getUserInfoFromToken, isAdmin } from './authService';
import CONFIG, { getApiUrl } from '../utils/config';

// Định nghĩa interface cho User
export interface User {
  id: number;
  username: string;
  stravaId: string | null;
  roles: string[] | null;
  createdAt: string | null;
}

// Định nghĩa interface cho thành viên trong nhóm
export interface TeamMember {
  id: number;
  firstname: string;
  lastname: string;
  membership: string;
  isAdmin: boolean;
  isOwner: boolean;
  isFromActivity: boolean;
  createdAt: string;
  updatedAt: string;
}

// Định nghĩa interface cho dữ liệu trả về
export interface Team {
  id: number;
  name: string;
  description?: string;
  admin: User;
  adminUsername?: string;
  createdAt: string;
  members: User[];
  membersCount?: number;
  totalDistance?: number;
  tournament?: {
    id: number;
    name: string;
  };
  type?: string;
}

// Interface cho dữ liệu tạo nhóm mới
export interface CreateTeamRequest {
  name: string;
  description: string;
  type: string;
  isPrivate: boolean;
  tournamentId?: number;
}

// Interface cho dữ liệu cập nhật nhóm
export interface UpdateTeamRequest {
  name: string;
  description: string;
  tournamentId?: number;
  adminId?: number;
}

// Base URL của API không cần nữa
// const API_BASE_URL = 'http://localhost:8080/api';

// Hàm lấy các nhóm của người dùng theo ID
export const getUserTeams = async (userId?: number): Promise<Team[]> => {
  try {
    // Nếu không cung cấp userId, lấy từ thông tin người dùng đã đăng nhập
    const actualUserId = userId || getUserId();
    
    if (!actualUserId) {
      throw new Error('ID người dùng không tồn tại');
    }
    
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.get(getApiUrl(`${CONFIG.ENDPOINTS.TEAMS.GET_USER_TEAMS}/${actualUserId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    console.log('Kết quả API lấy nhóm của người dùng:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Trả về mảng trống nếu không có dữ liệu hợp lệ
    return [];
  } catch (error) {
    console.error('Lỗi khi lấy dữ liệu nhóm:', error);
    return [];
  }
};

// Hàm tìm kiếm nhóm
export const searchTeams = async (query: string): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.get(getApiUrl(CONFIG.ENDPOINTS.TEAMS.SEARCH), {
      headers: {
        'Authorization': authHeader
      },
      params: { query }
    });
    
    console.log('Kết quả tìm kiếm nhóm:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Trả về mảng trống nếu không có dữ liệu hợp lệ
    return [];
  } catch (error) {
    console.error('Lỗi khi tìm kiếm nhóm:', error);
    return [];
  }
};

// Hàm ánh xạ từ User của authService sang User của teamsService
export const mapAuthUserToTeamsUser = (): User | null => {
  try {
    const authUser = getUserInfoFromToken();
    if (!authUser) {
      return null;
    }

    // Chuyển đổi từ User của authService sang User của teamsService
    return {
      id: authUser.userId ? Number(authUser.userId) : 0,
      username: authUser.username || '',
      stravaId: authUser.stravaId || null,
      roles: (authUser.roles as string[] | null) || null,
      createdAt: authUser.createdAt || null
    };
  } catch (error) {
    console.error('Lỗi khi chuyển đổi thông tin người dùng:', error);
    return null;
  }
};

// Lấy thông tin chi tiết của người dùng
export const getUserInfo = async (userId?: number): Promise<User> => {
  try {
    // Nếu không có userId hoặc userId trùng với ID người dùng hiện tại,
    // lấy thông tin từ token thay vì gọi API
    const currentUserId = getUserId();
    if (!userId || userId === currentUserId) {
      const userInfo = mapAuthUserToTeamsUser();
      if (userInfo) {
        return userInfo;
      }
    }
    
    // Nếu cần lấy thông tin người dùng khác hoặc không lấy được từ token, thì gọi API
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const actualUserId = userId || currentUserId;
    if (!actualUserId) {
      throw new Error('ID người dùng không tồn tại');
    }
    
    // Gọi API lấy thông tin người dùng
    const response = await axios.get(getApiUrl(`${CONFIG.ENDPOINTS.USERS.GET_BY_ID}/${actualUserId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    console.log('Kết quả API lấy thông tin người dùng:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      return response.data.data;
    }
    
    throw new Error('Không thể lấy thông tin người dùng');
  } catch (error) {
    console.error('Lỗi khi lấy thông tin người dùng:', error);
    throw error;
  }
};

// Hàm tạo nhóm mới
export const createTeam = async (teamData: CreateTeamRequest): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.post(getApiUrl(CONFIG.ENDPOINTS.TEAMS.CREATE), teamData, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Kết quả API tạo nhóm mới:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Không thể tạo nhóm mới');
  } catch (error) {
    console.error('Lỗi khi tạo nhóm mới:', error);
    throw error;
  }
};

// Hàm lấy tất cả nhóm
export const getAllTeams = async (): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.get(getApiUrl(CONFIG.ENDPOINTS.TEAMS.GET_ALL), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    console.log('Kết quả API lấy tất cả nhóm:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Trả về mảng trống nếu không có dữ liệu hợp lệ
    return [];
  } catch (error) {
    console.error('Lỗi khi lấy tất cả nhóm:', error);
    return [];
  }
};

// Hàm cập nhật thông tin nhóm
export const updateTeam = async (teamId: number, updateData: UpdateTeamRequest): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.put(getApiUrl(`${CONFIG.ENDPOINTS.TEAMS.UPDATE}/${teamId}`), updateData, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Kết quả API cập nhật nhóm:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu từ response.data.data
    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data?.message || 'Không thể cập nhật nhóm');
  } catch (error) {
    console.error('Lỗi khi cập nhật nhóm:', error);
    throw error;
  }
};

// Hàm xóa nhóm
export const deleteTeam = async (teamId: number): Promise<boolean> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.delete(getApiUrl(`${CONFIG.ENDPOINTS.TEAMS.DELETE}/${teamId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    console.log('Kết quả API xóa nhóm:', response.data);
    
    // Kiểm tra kết quả từ response
    if (response.data && response.data.result === "SUCCESS") {
      return true;
    }
    
    throw new Error(response.data?.message || 'Không thể xóa nhóm');
  } catch (error) {
    console.error('Lỗi khi xóa nhóm:', error);
    throw error;
  }
};

// Hàm lấy danh sách thành viên trong nhóm
export const getTeamMembers = async (teamId: number): Promise<TeamMember[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.get(getApiUrl(`/api/teams/${teamId}/members`), {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Kết quả API lấy thành viên trong nhóm:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu
    if (response.data && response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Trả về mảng trống nếu không có dữ liệu hợp lệ
    return [];
  } catch (error) {
    console.error('Lỗi khi lấy danh sách thành viên trong nhóm:', error);
    return [];
  }
};

// Hàm thêm thành viên vào nhóm bằng email
export const addTeamMemberByEmail = async (teamId: number, email: string): Promise<boolean> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.post(getApiUrl(`/api/teams/${teamId}/members/add-by-email`), 
      { email }, 
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Kết quả API thêm thành viên vào nhóm:', response.data);
    
    // Kiểm tra kết quả từ response
    if (response.data && response.data.result === "SUCCESS") {
      return true;
    }
    
    throw new Error(response.data?.message || 'Không thể thêm thành viên vào nhóm');
  } catch (error) {
    console.error('Lỗi khi thêm thành viên vào nhóm:', error);
    throw error;
  }
}; 

// Hàm lấy danh sách đội trong giải đấu
export const getTournamentTeams = async (tournamentId: number): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.get(getApiUrl(`/api/tournaments/${tournamentId}/teams`), {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Kết quả API lấy danh sách đội trong giải đấu:', response.data);
    
    // Kiểm tra cấu trúc response và lấy dữ liệu
    if (response.data && response.data.result === "SUCCESS" && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Trả về mảng trống nếu không có dữ liệu hợp lệ
    return [];
  } catch (error) {
    console.error(`Lỗi khi lấy danh sách đội trong giải đấu ${tournamentId}:`, error);
    return [];
  }
}; 